import React from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { AiOutlineArrowRight, AiOutlineDownload } from "react-icons/ai";
import profilePic from "../../Assets/profile_pic.jpeg";
import resume from "../../Assets/Rehman_Resume.pdf";
import cinnovaCertificate from "../../Assets/Certificates/Cinnova_Performance_Certificate.jpeg";
import nodeCertificate from "../../Assets/Certificates/nodejs_certificate.webp";
import employeeOfMonthCertificate from "../../Assets/Certificates/EOM_March_2024.jpg";
import awsAi from "../../Assets/Certificates/AWS_AI_Practitioner.png";
import awsAiEarlyAdopter from "../../Assets/Certificates/AWS_AI_Early_Adoptor_Badge.png";
import awsSolutionsArchitect from "../../Assets/Certificates/aws-certified-solutions-architect-associate.png";
import agenticAiTools from "../../Assets/Blogs/AgenticAiTools.png";
import mcpArchitecture from "../../Assets/Blogs/MCP_Architecture.png";
import sqlPagination from "../../Assets/Blogs/pagination_one_to_many_join.png";
import grpcProject from "../../Assets/Projects/microservice-architecture.png";
import nestProject from "../../Assets/Projects/nestjs_boiler_plate.png";
import portfolioProject from "../../Assets/Projects/portfolio.png";
import Particle from "../Particle";
import Home2 from "./Home2";
import Type from "./Type";

const featuredProjects = [
  {
    title: "NestJS Boilerplate",
    description: "A reusable NestJS starter with user CRUD, JWT authentication, and role-based access control.",
    image: nestProject,
  },
  {
    title: "Microservices with gRPC",
    description: "A multi-service API gateway and microservices example built with NestJS and Protocol Buffers.",
    image: grpcProject,
  },
  {
    title: "Portfolio",
    description: "A React portfolio bringing together my projects, technical writing, and certifications.",
    image: portfolioProject,
  },
];

const featuredBlogs = [
  {
    title: "Model Context Protocol (MCP)",
    description: "An introduction to MCP, the problems it solves, and its core components and architecture.",
    image: mcpArchitecture,
  },
  {
    title: "Pagination with One-to-Many Joins",
    description: "Why pagination gets tricky with nested relations, and how ORMs handle the challenge.",
    image: sqlPagination,
  },
  {
    title: "Tools and Agent Hand-offs",
    description: "A practical look at tools, orchestration, and hand-offs in agentic AI systems.",
    image: agenticAiTools,
  },
];

const badges = [
  { title: "AWS Certified Solutions Architect – Associate", image: awsSolutionsArchitect },
  { title: "AWS Certified AI Practitioner", image: awsAi },
  { title: "AWS AI Early Adopter", image: awsAiEarlyAdopter },
];

const featuredCertificates = [
  { title: "Performance Excellence Award", image: cinnovaCertificate },
  { title: "Node.js Internals", image: nodeCertificate },
  { title: "Employee of the Month", image: employeeOfMonthCertificate },
];

function PreviewSection({ title, description, items, link, linkLabel }) {
  return (
    <section className="home-preview-section">
      <div className="home-section-heading">
        <div>
          <p className="home-eyebrow">A closer look</p>
          <h2>{title}</h2>
          <p className="home-section-description">{description}</p>
        </div>
        <Button as={Link} to={link} variant="outline-light" className="home-text-link">
          {linkLabel} <AiOutlineArrowRight aria-hidden="true" />
        </Button>
      </div>
      <Row className="g-4">
        {items.map((item) => (
          <Col key={item.title} md={4}>
            <Link to={link} className="home-preview-card">
              <img src={item.image} alt="" loading="lazy" />
              <div className="home-preview-card-copy">
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <span>Explore <AiOutlineArrowRight aria-hidden="true" /></span>
              </div>
            </Link>
          </Col>
        ))}
      </Row>
    </section>
  );
}

function Home() {
  return (
    <main className="home-page">
      <section className="home-hero" id="home">
        <Particle />
        <Container className="home-hero-content">
          <Row className="align-items-center gy-5">
            <Col lg={7}>
              <p className="home-eyebrow">
                Senior Software Engineer | AI Solutions,
                <br />
                Agentic Systems &amp; Cloud Engineering
              </p>
              <h1>
                Hi, I&apos;m <span>Rehman Akram.</span>
              </h1>
              <div className="home-hero-role"><Type /></div>
              <p className="home-hero-summary">
                I build reliable web products and explore practical applications of AI,
                from backend architecture to thoughtful user experiences.
              </p>
              <div className="home-hero-actions">
                <Button as={Link} to="/project" className="home-primary-button">
                  Explore my work <AiOutlineArrowRight aria-hidden="true" />
                </Button>
                <Button
                  href={resume}
                  download="Rehman_Akram_Resume.pdf"
                  variant="outline-light"
                  className="home-secondary-button"
                >
                  <AiOutlineDownload aria-hidden="true" /> Download resume
                </Button>
              </div>
              <div className="home-hero-badges" aria-label="AWS badges">
                {badges.map((badge) => (
                  <Link
                    key={badge.title}
                    to="/certificates"
                    aria-label={`View ${badge.title}`}
                  >
                    <img src={badge.image} alt={badge.title} loading="lazy" />
                  </Link>
                ))}
              </div>
            </Col>
            <Col lg={5} className="home-hero-portrait-column">
              <div className="home-hero-portrait-wrap">
                <img src={profilePic} alt="Rehman Akram" className="home-hero-portrait" />
                <div className="home-portrait-caption">
                  <span className="home-status-dot" />
                  Building software with purpose
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="home-badges-section" aria-labelledby="home-certificates-title">
        <Container>
          <div className="home-section-heading home-badges-heading">
            <div>
              <h2 id="home-certificates-title">Certifications &amp; Rewards</h2>
            </div>
            <Button as={Link} to="/certificates" variant="outline-light" className="home-text-link">
              View all <AiOutlineArrowRight aria-hidden="true" />
            </Button>
          </div>
          <Row className="g-3 justify-content-center">
            {featuredCertificates.map((certificate) => (
              <Col key={certificate.title} xs={12} md={4}>
                <Link
                  to="/certificates"
                  className="home-badge-card home-certificate-card"
                  aria-label={`View ${certificate.title}`}
                >
                  <img src={certificate.image} alt={certificate.title} loading="lazy" />
                  <span>{certificate.title}</span>
                </Link>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <Home2 />

      <Container className="home-showcase">
        <PreviewSection
          title="Selected projects"
          description="A few things I’ve built, from reusable developer tools to distributed services."
          items={featuredProjects}
          link="/project"
          linkLabel="All projects"
        />
        <PreviewSection
          title="Ideas, explained"
          description="Notes and practical guides on AI systems, backend engineering, and software architecture."
          items={featuredBlogs}
          link="/blogs"
          linkLabel="All articles"
        />
      </Container>

      <section className="home-contact-section">
        <Container>
          <p className="home-eyebrow">Have a project in mind?</p>
          <h2>Let&apos;s build something useful.</h2>
          <p>Explore my experience, credentials, and the work I&apos;ve shared.</p>
          <Button as={Link} to="/about" className="home-primary-button">
            More about me <AiOutlineArrowRight aria-hidden="true" />
          </Button>
        </Container>
      </section>
    </main>
  );
}

export default Home;
